import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, Loader2Icon, UserIcon, DatabaseIcon, ShieldIcon, SettingsIcon } from 'lucide-react';
import { StepProps } from '../../types/onboarding';
interface ReviewCreateProps extends StepProps {
  onBack: () => void;
}
export function ReviewCreate({
  data,
  setCanAdvance,
  onBack
}: ReviewCreateProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  useEffect(() => {
    setCanAdvance(true);
  }, [setCanAdvance]);
  const handleCreate = () => {
    setIsCreating(true);
    setTimeout(() => {
      setIsCreating(false);
      setIsSuccess(true);
    }, 1500);
  };
  const isLoanOfficer = data.profile.includes('Loan Officer');
  const isProcessor = data.role.includes('Processor');
  const hasEinstein = data.permissionSets.includes('Loan Officer Permission Set') || data.permissionSets.includes('Processor Permission Set');
  const DataRow = ({
    label,
    value
  }: {
    label: string;
    value: any;
  }) => <div className="py-2 flex justify-between border-b border-gray-100 last:border-0" data-id="element-447" data-mp="2sp6yrq497q3t">
      <span className="text-sm text-gray-500" data-id="element-448" data-mp="2xjb6iblcg25y">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right max-w-[60%] truncate" data-id="element-449" data-mp="2itvhp15di6sw">
        {value || <span className="text-gray-400 italic" data-id="element-450" data-mp="2nrdvb4uc5a78">None</span>}
      </span>
    </div>;
  if (isSuccess) {
    return <div className="py-12 text-center flex flex-col items-center justify-center min-h-[400px]" data-id="element-451" data-mp="3bj2sayi3qmui">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20
      }} className="w-20 h-20 bg-[#2E844A] rounded-full flex items-center justify-center shadow-lg mb-6" data-id="element-452" data-mp="3gfjgfspax17l">
          <CheckIcon className="w-10 h-10 text-white" data-id="element-453" data-mp="1v4160mwsioa8" />
        </motion.div>
        <motion.h2 initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-2xl font-bold text-gray-900 mb-2" data-id="element-454" data-mp="1v15b77g8ofq0">
          User Successfully Created!
        </motion.h2>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-sm text-gray-600 mb-10" data-id="element-455" data-mp="3nysonim2mtv2">
          {data.firstName} {data.lastName} ({data.username}) has been fully
          onboarded.
        </motion.p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 text-left w-full max-w-2xl" data-id="element-456" data-mp="39zau0mm3gmds">
          {[{
          icon: UserIcon,
          count: 1,
          label: 'User'
        }, {
          icon: DatabaseIcon,
          count: 1,
          label: 'Account'
        }, {
          icon: ShieldIcon,
          count: data.permissionSets.length,
          label: 'Perm Sets'
        }, {
          icon: SettingsIcon,
          count: isLoanOfficer ? 4 : 3,
          label: 'Metadata'
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + i * 0.1
        }} className="bg-white border border-gray-100 rounded-xl p-4 flex items-center space-x-4 shadow-sm" data-id="element-457" data-mp="1fk40eir5404w" data-mpi={i}>
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-458" data-mp="wsl1an08l685">
                <item.icon className="w-5 h-5 text-navy-600" data-id="element-459" data-mp="11qwcb129pmry" />
              </div>
              <div data-id="element-460" data-mp="288rt2ze7xrsh">
                <div className="text-xl font-bold text-gray-900 leading-none" data-id="element-461" data-mp="28bdxiq5tbr6h">
                  {item.count}
                </div>
                <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mt-1" data-id="element-462" data-mp="2asxvv3r3jc7g">
                  {item.label}
                </div>
              </div>
            </motion.div>)}
        </div>

        <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8
      }} onClick={() => window.location.reload()} className="px-6 py-2.5 bg-white border border-gray-300 text-sm text-gray-700 font-medium rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-navy-600 transition-colors" data-id="element-463" data-mp="2ksge9frcypxe">
          Start New Onboarding
        </motion.button>
      </div>;
  }
  return <div className="flex flex-col h-full" data-id="element-464" data-mp="3dgca6i93sdt4">
      <div className="flex-1 space-y-8" data-id="element-465" data-mp="3dh45rt2pabje">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-466" data-mp="3ddyce1n1gnhv">
          <div data-id="element-467" data-mp="38j2kd1hsy04r">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-468" data-mp="38feq204j99oz">
              User & Account
            </h4>
            <DataRow label="Name" value={`${data.firstName} ${data.lastName}`} data-id="element-469" data-mp="3w437cwhmiuzd" />
            <DataRow label="Alias" value={data.alias} data-id="element-470" data-mp="3i6y2v0xk6468" />
            <DataRow label="Email" value={data.email} data-id="element-471" data-mp="3n2vkrbogzil4" />
            <DataRow label="Username" value={data.username} data-id="element-472" data-mp="3n5rgctcuhv6e" />
            <DataRow label="Title" value={data.title} data-id="element-473" data-mp="38dhb02gzez57" />
            <DataRow label="Role" value={data.role} data-id="element-474" data-mp="3d5ilpjyi4ht4" />
            <DataRow label="Profile" value={data.profile} data-id="element-475" data-mp="3d71z92srwer7" />
            <DataRow label="Enhanced Note Role" value={data.enhancedNoteRole} data-id="element-476" data-mp="2zf5cle9vix27" />
            <DataRow label="Team Lead" value={data.teamLead} data-id="element-477" data-mp="34d74mopa26pt" />
          </div>
          <div data-id="element-478" data-mp="34b2q7ctr5pba">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-479" data-mp="2pgg4gd7s4qz5">
              Contact & Integrations
            </h4>
            <DataRow label="Phone" value={data.phone} data-id="element-480" data-mp="2uh3sqr4mitpo" />
            <DataRow label="Mobile" value={data.mobile} data-id="element-481" data-mp="2bbphg3urudew" />
            <DataRow label="Fax" value={data.fax} data-id="element-482" data-mp="26jxs1wncqdqc" />
            <DataRow label="Extension" value={data.extension} data-id="element-483" data-mp="26j5x8o1kwk14" />
            <DataRow label="RingCentral Ext. Id" value={data.ringCentralExtensionId} data-id="element-484" data-mp="21kbflpfcl1il" />
            <DataRow label="Bonzo" value={data.isBonzoUser ? data.bonzoId || 'Enabled' : ''} data-id="element-485" data-mp="1wm03f81kyex7" />
            <DataRow label="NMLS #" value={data.nmlsNumber} data-id="element-486" data-mp="2u8gwg13a66d5" />
            <DataRow label="LendingPad User Id" value={data.lendingPadUserId} data-id="element-487" data-mp="2paovyioklbxc" />
            <DataRow label="Application Link" value={data.applicationLink} data-id="element-488" data-mp="2p8kvkapvqv1t" />
          </div>
          <div data-id="element-489" data-mp="2kdf8v9onlcco">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-490" data-mp="2fc9hpgan5x59">
              Access
            </h4>
            <DataRow label="Packages" value={data.packageLicenses.length ? `${data.packageLicenses.length} selected` : 'None'} data-id="element-491" data-mp="18xqlih7hmbbc" />
            <DataRow label="Perm Sets" value={data.permissionSets.length ? `${data.permissionSets.length} selected` : 'None'} data-id="element-492" data-mp="3kjiqn1gpcjyu" />
          </div>
          <div data-id="element-493" data-mp="3ua3x1b2massb">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-494" data-mp="3uan4fxun5ys3">
              Automated Metadata
            </h4>
            <ul className="text-sm text-gray-700 space-y-1.5 list-disc list-inside py-2" data-id="element-495" data-mp="73l5btuts27e">
              <li data-id="element-496" data-mp="c3fqrhvtzzl2">Round Robin Rules</li>
              <li data-id="element-497" data-mp="c1mlo70v9nf6">Shift Definitions</li>
              <li data-id="element-498" data-mp="h3byns7ngb5w">Commissions ({isProcessor ? 'Processor' : 'Standard'})</li>
              {isLoanOfficer && <li data-id="element-499" data-mp="2dvc4my5h1lj0">Weighting Rules</li>}
              {hasEinstein && <li data-id="element-500" data-mp="2dwwzkovp76he">Einstein Activity Capture</li>}
            </ul>
          </div>
        </div>
      </div>

      {/* Inline Navigation for Review Step */}
      <div className="mt-12 pt-6 border-t border-gray-100 flex items-center justify-between" data-id="element-501" data-mp="olt9c1r9hw0g">
        <button onClick={onBack} className="text-sm font-medium text-gray-500 hover:text-gray-900 hover:underline transition-colors" data-id="element-502" data-mp="jnrtdpp4uct7">
          Previous
        </button>

        <button onClick={handleCreate} disabled={isCreating} className="flex items-center justify-center px-8 py-2.5 bg-navy-600 text-white text-sm font-medium rounded-full shadow-sm hover:bg-navy-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-600 disabled:opacity-70 disabled:cursor-not-allowed transition-colors duration-150 ease-out" data-id="element-503" data-mp="tci4br25hb4c">
          {isCreating ? <>
              <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-504" data-mp="al8rb7t68480" />
              Creating User...
            </> : 'Create User'}
        </button>
      </div>
    </div>;
}